// useWallSocket — the wall dashboard's live connection (API routes/wall).
// Holds the latest snapshot and a merged activity feed (the snapshot's
// backlog plus every activity frame since), and reconnects with backoff.
// A 4401 close means the token is no longer valid; that is not retried.

import { useEffect, useRef, useState } from 'react'
import type { WallActivity, WallServerMessage, WallSnapshot } from '@goblic/shared'
import { wallApi } from '../lib/api-client'

export type WallSocketStatus = 'connecting' | 'open' | 'reconnecting' | 'unauthorized'

const FEED_MAX = 120
const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000]

function mergeFeed(current: WallActivity[], incoming: WallActivity[]): WallActivity[] {
  const byId = new Map<string, WallActivity>()
  for (const item of current) byId.set(item.id, item)
  for (const item of incoming) byId.set(item.id, item)
  return [...byId.values()]
    .sort((a, b) => Number(BigInt(b.id) - BigInt(a.id)))
    .slice(0, FEED_MAX)
}

export function useWallSocket(): {
  status: WallSocketStatus
  snapshot: WallSnapshot | null
  /** Newest first. */
  feed: WallActivity[]
  lastMessageAt: number | null
} {
  const [status, setStatus] = useState<WallSocketStatus>('connecting')
  const [snapshot, setSnapshot] = useState<WallSnapshot | null>(null)
  const [feed, setFeed] = useState<WallActivity[]>([])
  const [lastMessageAt, setLastMessageAt] = useState<number | null>(null)
  const attempt = useRef(0)

  useEffect(() => {
    let socket: WebSocket | null = null
    let retry: ReturnType<typeof setTimeout> | null = null
    let disposed = false

    const connect = (): void => {
      socket = new WebSocket(wallApi.wsUrl())
      socket.onopen = () => {
        attempt.current = 0
        setStatus('open')
      }
      socket.onmessage = (frame) => {
        let message: WallServerMessage
        try {
          message = JSON.parse(String(frame.data)) as WallServerMessage
        } catch {
          return
        }
        setLastMessageAt(Date.now())
        if (message.type === 'snapshot') {
          setSnapshot(message.snapshot)
          setFeed((current) => mergeFeed(current, message.snapshot.activity))
        } else if (message.type === 'activity') {
          setFeed((current) => mergeFeed(current, [message.activity]))
        }
      }
      socket.onclose = (event) => {
        if (disposed) return
        if (event.code === 4401) {
          setStatus('unauthorized')
          return
        }
        setStatus('reconnecting')
        const delay = BACKOFF_MS[Math.min(attempt.current, BACKOFF_MS.length - 1)]!
        attempt.current += 1
        retry = setTimeout(connect, delay)
      }
    }

    connect()
    return () => {
      disposed = true
      if (retry) clearTimeout(retry)
      socket?.close()
    }
  }, [])

  return { status, snapshot, feed, lastMessageAt }
}
